/* ============================================================================
   HEADER SECTION — header.js
   ----------------------------------------------------------------------------
   Vanilla JS. Per-header init so multiple sections work. Re-runs on
   shopify:section:load for theme-editor live reload.

   Handles:
     - Marquee seamless duplication until track ≥ 2× viewport
     - Desktop dropdown ARIA + tap-on-touch (touch devices)
     - Mobile hamburger drawer (focus trap, ESC, scrim)
     - Search drawer (focus input, ESC, scrim)
     - Mobile drawer sub-list expand/collapse
     - Cart-count refresh helper (window.SiteHeader.refreshCart())
   ========================================================================= */

(function () {
  'use strict';

  var MOBILE_BREAKPOINT = 768;
  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function isMobile() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  // -------------------------------------------------------------------------
  // MARQUEE
  // -------------------------------------------------------------------------
  function initMarquee(root) {
    var marquees = root.querySelectorAll('[data-marquee]');
    marquees.forEach(function (marquee) {
      var track = marquee.querySelector('[data-marquee-track]');
      if (!track) return;

      // Skip duplication entirely when reduced motion — let it scroll horizontally if needed.
      if (prefersReduced.matches) return;

      // Snapshot original children once.
      var originals = Array.from(track.children);
      if (originals.length === 0) return;

      // Remove any previous duplicates (in case of re-init).
      track.innerHTML = '';
      originals.forEach(function (node) {
        track.appendChild(node.cloneNode(true));
      });

      // Duplicate group until the *original* group width covers viewport.
      // We always need the track to contain at least TWO identical groups for the
      // -50% keyframe to land seamlessly.
      var ensureFill = function () {
        // First, make sure single-group width ≥ viewport.
        // Reset by removing all children and re-adding the original set, then doubling.
        track.innerHTML = '';
        var group = document.createElement('span');
        group.style.display = 'inline-flex';
        group.style.alignItems = 'center';
        originals.forEach(function (n) { group.appendChild(n.cloneNode(true)); });
        track.appendChild(group);

        var viewport = marquee.getBoundingClientRect().width;
        // Multiply the group until it covers the viewport.
        // (We measure after each append.)
        var safety = 0;
        while (group.getBoundingClientRect().width < viewport && safety < 20) {
          originals.forEach(function (n) { group.appendChild(n.cloneNode(true)); });
          safety++;
        }
        // Now append a SECOND identical group so the -50% keyframe is seamless.
        track.appendChild(group.cloneNode(true));
      };

      ensureFill();

      // Re-measure on resize (debounced).
      var resizeTimer;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(ensureFill, 200);
      });
    });
  }

  // -------------------------------------------------------------------------
  // DESKTOP DROPDOWNS (ARIA + touch-tap support)
  // -------------------------------------------------------------------------
  function initDropdowns(root) {
    var items = root.querySelectorAll('[data-nav-item]');

    items.forEach(function (item) {
      var trigger = item.querySelector('[data-nav-trigger]');
      var dropdown = item.querySelector('[data-nav-dropdown]');
      if (!trigger || !dropdown) return;

      // Hover sets aria-expanded for accessibility.
      item.addEventListener('mouseenter', function () {
        if (isMobile()) return;
        trigger.setAttribute('aria-expanded', 'true');
        dropdown.setAttribute('aria-hidden', 'false');
        item.setAttribute('data-open', 'true');
      });
      item.addEventListener('mouseleave', function () {
        if (isMobile()) return;
        trigger.setAttribute('aria-expanded', 'false');
        dropdown.setAttribute('aria-hidden', 'true');
        item.removeAttribute('data-open');
      });

      // Touch / keyboard — tap to toggle on touch devices that don't hover.
      var supportsHover = window.matchMedia('(hover: hover)').matches;
      trigger.addEventListener('click', function (e) {
        if (supportsHover && !isMobile()) return; // let hover handle it; click follows the link
        e.preventDefault();
        var open = item.getAttribute('data-open') === 'true';
        // Close siblings
        items.forEach(function (other) {
          if (other !== item) {
            other.removeAttribute('data-open');
            var t = other.querySelector('[data-nav-trigger]');
            if (t) t.setAttribute('aria-expanded', 'false');
            var d = other.querySelector('[data-nav-dropdown]');
            if (d) d.setAttribute('aria-hidden', 'true');
          }
        });
        if (open) {
          item.removeAttribute('data-open');
          trigger.setAttribute('aria-expanded', 'false');
          dropdown.setAttribute('aria-hidden', 'true');
        } else {
          item.setAttribute('data-open', 'true');
          trigger.setAttribute('aria-expanded', 'true');
          dropdown.setAttribute('aria-hidden', 'false');
        }
      });

      // Close dropdown on Escape from a focused child link.
      dropdown.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          item.removeAttribute('data-open');
          trigger.setAttribute('aria-expanded', 'false');
          dropdown.setAttribute('aria-hidden', 'true');
          trigger.focus();
        }
      });
    });

    // Click outside closes any open dropdown.
    document.addEventListener('click', function (e) {
      items.forEach(function (item) {
        if (!item.contains(e.target) && item.getAttribute('data-open') === 'true') {
          item.removeAttribute('data-open');
          var t = item.querySelector('[data-nav-trigger]');
          if (t) t.setAttribute('aria-expanded', 'false');
          var d = item.querySelector('[data-nav-dropdown]');
          if (d) d.setAttribute('aria-hidden', 'true');
        }
      });
    });
  }

  // -------------------------------------------------------------------------
  // MOBILE NAV DRAWER
  // -------------------------------------------------------------------------
  function initDrawer(root) {
    var toggle = root.querySelector('[data-menu-toggle]');
    var drawer = root.querySelector('[data-menu-drawer]');
    var close = root.querySelector('[data-menu-close]');
    var scrim = root.querySelector('[data-scrim]');
    if (!toggle || !drawer) return;

    var open = function () {
      drawer.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      if (scrim) scrim.setAttribute('data-active', 'true');
      document.body.classList.add('site-header-drawer-open');
      // Move focus to close button for keyboard users.
      setTimeout(function () { if (close) close.focus(); }, 50);
    };
    var shut = function () {
      drawer.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      if (scrim) scrim.setAttribute('data-active', 'false');
      document.body.classList.remove('site-header-drawer-open');
      toggle.focus();
    };

    toggle.addEventListener('click', function () {
      if (drawer.getAttribute('aria-hidden') === 'true') open();
      else shut();
    });
    if (close) close.addEventListener('click', shut);
    if (scrim) scrim.addEventListener('click', function () {
      if (drawer.getAttribute('aria-hidden') === 'false') shut();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') {
        shut();
      }
    });

    // Sublist toggles inside the drawer.
    var subtoggles = drawer.querySelectorAll('[data-drawer-toggle]');
    subtoggles.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var sublist = btn.nextElementSibling;
        if (!sublist) return;
        var isOpen = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        if (isOpen) {
          sublist.setAttribute('hidden', '');
        } else {
          sublist.removeAttribute('hidden');
        }
      });
    });

    // Close drawer if resized into desktop view.
    window.addEventListener('resize', function () {
      if (!isMobile() && drawer.getAttribute('aria-hidden') === 'false') {
        shut();
      }
    });
  }

  // -------------------------------------------------------------------------
  // SEARCH DRAWER
  // -------------------------------------------------------------------------
  function initSearch(root) {
    var toggle = root.querySelector('[data-search-toggle]');
    var drawer = root.querySelector('[data-search-drawer]');
    var close = root.querySelector('[data-search-close]');
    var scrim = root.querySelector('[data-scrim]');
    if (!toggle || !drawer) return;

    var input = drawer.querySelector('input[type="search"]');

    var open = function () {
      drawer.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      if (scrim) scrim.setAttribute('data-active', 'true');
      document.body.classList.add('site-header-drawer-open');
      setTimeout(function () { if (input) input.focus(); }, 80);
    };
    var shut = function () {
      drawer.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      if (scrim) scrim.setAttribute('data-active', 'false');
      // Only release body scroll if no other drawer is open.
      var menuDrawer = root.querySelector('[data-menu-drawer]');
      if (!menuDrawer || menuDrawer.getAttribute('aria-hidden') === 'true') {
        document.body.classList.remove('site-header-drawer-open');
      }
      toggle.focus();
    };

    toggle.addEventListener('click', function () {
      if (drawer.getAttribute('aria-hidden') === 'true') open();
      else shut();
    });
    if (close) close.addEventListener('click', shut);
    if (scrim) scrim.addEventListener('click', function () {
      if (drawer.getAttribute('aria-hidden') === 'false') shut();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') {
        shut();
      }
    });
  }

  // -------------------------------------------------------------------------
  // CART COUNT REFRESH HELPER
  // -------------------------------------------------------------------------
  function refreshCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      if (typeof count === 'number') {
        el.textContent = count;
        if (count > 0) el.removeAttribute('hidden');
        else el.setAttribute('hidden', '');
      }
    });
  }

  function fetchCartCount() {
    if (!window.fetch) return;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) { if (cart) refreshCartCount(cart.item_count); })
      .catch(function () {});
  }

  // -------------------------------------------------------------------------
  // INIT
  // -------------------------------------------------------------------------
  function initHeader(root) {
    if (!root || root.dataset.initialised === 'true') return;
    root.dataset.initialised = 'true';
    initMarquee(root);
    initDropdowns(root);
    initDrawer(root);
    initSearch(root);
  }

  function bootstrap() {
    document.querySelectorAll('[data-header]').forEach(initHeader);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  // Shopify section-editor: re-init when section reloads.
  document.addEventListener('shopify:section:load', function (e) {
    var header = e.target.querySelector('[data-header]');
    if (header) {
      header.dataset.initialised = 'false';
      initHeader(header);
    }
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var header = e.target.querySelector('[data-header]');
    if (header) header.dataset.initialised = 'false';
  });

  // Expose a tiny public API.
  window.SiteHeader = {
    refreshCart: fetchCartCount,
    setCartCount: refreshCartCount
  };
})();
